import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Play, Menu, ChevronRight, ChevronDown, Images, X, User, CreditCard } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import ImageGallery from "@/components/ImageGallery";
import VideoPresentation from "@/components/VideoPresentation";
import VideoPlayer from "@/components/VideoPlayer";
import SubscriptionManager from "@/components/SubscriptionManager";
import memoraImages from "@/data/memoraImages";

const Index = () => {
  const { user, signIn, signUp, signOut, hasActiveSubscription, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDocsOpen, setIsDocsOpen] = useState(false);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showGetStartedModal, setShowGetStartedModal] = useState(false);
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [signUpForm, setSignUpForm] = useState({ email: '', password: '', fullName: '' });
  const docsRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (docsRef.current && !docsRef.current.contains(event.target as Node)) {
        setIsDocsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const { error } = await signIn(loginForm.email, loginForm.password);
    
    if (error) {
      toast({
        title: "Login Failed",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Welcome back!",
        description: "You have successfully logged in.",
      });
      setShowLoginModal(false);
      setLoginForm({ email: '', password: '' });
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    const { error } = await signUp(signUpForm.email, signUpForm.password, {
      full_name: signUpForm.fullName
    });
    
    if (error) {
      toast({
        title: "Sign Up Failed",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Account Created!",
        description: "Please check your email to verify your account. Your 7-day free trial has started!",
      });
      setShowSignUpModal(false);
      setSignUpForm({ email: '', password: '', fullName: '' });
    }
  };

  const handleGetStarted = () => {
    if (user) {
      if (!hasActiveSubscription) {
        setShowSubscriptionModal(true);
      } else {
        setShowGetStartedModal(true);
      }
    } else {
      setShowSignUpModal(true);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Navigation */}
      <nav className="flex items-center justify-between p-4 lg:px-8">
        <div className="flex items-center space-x-8">
          <h1 className="text-2xl font-bold text-white">MEMORA-AI</h1>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6">
            <a href="https://github.com/gabrieltemtsen/memora-ai" className="text-gray-300 hover:text-white transition-colors">
              API Reference
            </a>
            <span className="text-gray-400 text-sm">Use our API</span>
            
            <a href="#" className="text-gray-300 hover:text-white transition-colors">
              Developers
            </a>
            <span className="text-gray-400 text-sm">Build on Memora</span>
            
            <div className="relative" ref={docsRef}>
              <button 
                onClick={() => setIsDocsOpen(!isDocsOpen)}
                className="text-gray-300 hover:text-white transition-colors flex items-center space-x-1"
              >
                <span>Docs</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              
              {/* Docs Dropdown */}
              {isDocsOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-slate-800 border border-gray-700 rounded-lg shadow-lg z-50">
                  <div className="p-2 space-y-1">
                    <a href="#" className="block px-3 py-2 text-gray-300 hover:text-white hover:bg-slate-700 rounded transition-colors">
                      Getting Started
                    </a>
                    <a href="#" className="block px-3 py-2 text-gray-300 hover:text-white hover:bg-slate-700 rounded transition-colors">
                      API Documentation
                    </a>
                    <a href="#" className="block px-3 py-2 text-gray-300 hover:text-white hover:bg-slate-700 rounded transition-colors">
                      Examples
                    </a>
                    <a href="#" className="block px-3 py-2 text-gray-300 hover:text-white hover:bg-slate-700 rounded transition-colors">
                      Support
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Menu Button */}
        <div className="lg:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="text-white"
          >
            <Menu className="w-6 h-6" />
          </Button>
        </div>

        {/* Desktop Login/User Menu */}
        <div className="hidden lg:block">
          {user ? (
            <div className="flex items-center space-x-3">
              {!hasActiveSubscription && (
                <Button
                  onClick={() => setShowSubscriptionModal(true)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Upgrade
                </Button>
              )}
              <Button
                onClick={() => setShowSubscriptionModal(true)}
                variant="outline"
                className="text-white border-white hover:bg-white hover:text-black"
              >
                <User className="w-4 h-4 mr-2" />
                Account
              </Button>
              <Button
                onClick={signOut}
                variant="ghost"
                className="text-gray-400 hover:text-white"
              >
                Sign Out
              </Button>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <Button
                onClick={() => setShowLoginModal(true)}
                variant="ghost"
                className="text-white hover:bg-white/20"
              >
                Login
              </Button>
              <Button
                onClick={() => setShowSignUpModal(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                Start Free Trial
              </Button>
            </div>
          )}
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-slate-800 p-4 space-y-4">
          <a href="https://github.com/gabrieltemtsen/memora-ai" className="block text-gray-300 hover:text-white">
            API Reference
          </a>
          <a href="#" className="block text-gray-300 hover:text-white">
            Developers
          </a>
          <button 
            onClick={() => setIsDocsOpen(!isDocsOpen)}
            className="block w-full text-left text-gray-300 hover:text-white"
          >
            Docs
          </button>
          {isDocsOpen && (
            <div className="ml-4 space-y-2">
              <a href="#" className="block text-gray-400 hover:text-white text-sm">Getting Started</a>
              <a href="#" className="block text-gray-400 hover:text-white text-sm">API Documentation</a>
              <a href="#" className="block text-gray-400 hover:text-white text-sm">Examples</a>
              <a href="#" className="block text-gray-400 hover:text-white text-sm">Support</a>
            </div>
          )}
          <Button 
            onClick={() => setShowLoginModal(true)}
            variant="outline" 
            className="w-full text-white border-white hover:bg-white hover:text-black"
          >
            Login
          </Button>
        </div>
      )}

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-4xl lg:text-6xl font-bold text-white leading-tight">
                <span className="text-purple-400">Bridging,</span>
                <br />
                <span className="text-white">Beyond Life!</span>
              </h2>
              
              <p className="text-lg text-gray-300 leading-relaxed">
                We preserve and share important memories across generations. We provide an immersive trip for both users who wish to develop their AI presence beyond death, leaving a lasting legacy, and users who want to commune with the departed, cherishing and learning from these saved memories. MEMORA-AI bridges the time-space gap by combining intuitive design, AI interactions, and a strong ethical framework, linking hearts via shared experiences and tales.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={handleGetStarted}
                size="lg" 
                className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3"
              >
                {user ? (hasActiveSubscription ? 'Get Started' : 'Start Free Trial') : 'Start Free Trial'}
              </Button>
              
              <Button 
                onClick={() => setShowHowItWorksModal(true)}
                variant="outline" 
                size="lg" 
                className="text-white border-white hover:bg-white hover:text-black px-8 py-3 flex items-center space-x-2"
              >
                <Play className="w-5 h-5" />
                <span>How It Works</span>
              </Button>

              <Button 
                variant="outline" 
                size="lg" 
                onClick={() => setShowImageGallery(!showImageGallery)}
                className="text-purple-400 border-purple-400 hover:bg-purple-400 hover:text-white px-8 py-3 flex items-center space-x-2"
              >
                <Images className="w-5 h-5" />
                <span>{showImageGallery ? 'Show Video' : 'View Gallery'}</span>
              </Button>
            </div>

            {/* Coming Soon Badges */}
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Badge variant="secondary" className="bg-gray-700 text-gray-300">
                  coming soon
                </Badge>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </div>
              <div className="flex items-center space-x-3">
                <Badge variant="secondary" className="bg-gray-700 text-gray-300">
                  coming soon
                </Badge>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </div>
            </div>
          </div>

          {/* Right Content - Watermark-Free Presentation or Image Gallery */}
          <div className="relative">
            {/* Background Blob */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-3xl"></div>
            
            {showImageGallery ? (
              /* Image Gallery */
              <div className="relative">
                <ImageGallery images={memoraImages} />
              </div>
            ) : (
              /* Original YouTube Video with Sound */
              <Card className="relative bg-black/50 backdrop-blur-sm border-gray-700 overflow-hidden">
                <div className="aspect-video relative">
                  <iframe
                    src="https://www.youtube.com/embed/xe-JnlzGuqM?si=Ya4m4cq9bSt1p6gM&autoplay=0&controls=1"
                    title="Memora-AI: Bridging Beyond Life"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-700 mt-20">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0">
            <div className="text-center lg:text-left">
              <p className="text-white font-semibold">Memora-AI</p>
              <p className="text-gray-400 text-sm">© 2024 Memora-AI. All rights reserved</p>
            </div>
            
            {/* Social Links */}
            <div className="flex space-x-6">
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                <span className="sr-only">Twitter</span>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84"/>
                </svg>
              </a>
              
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                <span className="sr-only">YouTube</span>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
              
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                <span className="sr-only">Instagram</span>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987c6.62 0 11.987-5.367 11.987-11.987C24.014 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.611-3.132-1.551-.684-.94-.684-2.08 0-3.02.684-.94 1.835-1.551 3.132-1.551 1.297 0 2.448.611 3.132 1.551.684.94.684 2.08 0 3.02-.684.94-1.835 1.551-3.132 1.551zm7.718 0c-1.297 0-2.448-.611-3.132-1.551-.684-.94-.684-2.08 0-3.02.684-.94 1.835-1.551 3.132-1.551 1.297 0 2.448.611 3.132 1.551.684.94.684 2.08 0 3.02-.684.94-1.835 1.551-3.132 1.551z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">Login to Memora AI</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowLoginModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="space-y-4">
              <p className="text-gray-300">Choose your login method:</p>
              <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white">
                Continue with Affinidi
              </Button>
              <Button variant="outline" className="w-full text-white border-gray-600 hover:bg-gray-700">
                Continue with Email
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Get Started Modal */}
      {showGetStartedModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">Get Started with Memora AI</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowGetStartedModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="space-y-4">
              <p className="text-gray-300">
                Welcome to Memora AI! Start preserving your memories and creating your digital legacy today.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <Card className="bg-slate-700 border-gray-600 p-4">
                  <h3 className="text-white font-semibold mb-2">Create Your AI Presence</h3>
                  <p className="text-gray-300 text-sm">
                    Build your digital legacy that will live beyond your physical presence.
                  </p>
                </Card>
                <Card className="bg-slate-700 border-gray-600 p-4">
                  <h3 className="text-white font-semibold mb-2">Connect with Memories</h3>
                  <p className="text-gray-300 text-sm">
                    Commune with departed loved ones through preserved memories and AI interactions.
                  </p>
                </Card>
              </div>
              <Button 
                onClick={() => setShowLoginModal(true)}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              >
                Start Your Journey
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* How It Works Modal */}
      {showHowItWorksModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-lg p-6 w-full max-w-3xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">How Memora AI Works</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowHowItWorksModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                <Card className="bg-slate-700 border-gray-600 p-4 text-center">
                  <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <h3 className="text-white font-semibold mb-2">Preserve Memories</h3>
                  <p className="text-gray-300 text-sm">
                    Upload photos, videos, voice recordings, and stories to create your digital memory vault.
                  </p>
                </Card>
                <Card className="bg-slate-700 border-gray-600 p-4 text-center">
                  <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <h3 className="text-white font-semibold mb-2">AI Processing</h3>
                  <p className="text-gray-300 text-sm">
                    Our AI learns from your memories to create an authentic digital representation of you.
                  </p>
                </Card>
                <Card className="bg-slate-700 border-gray-600 p-4 text-center">
                  <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <h3 className="text-white font-semibold mb-2">Connect & Share</h3>
                  <p className="text-gray-300 text-sm">
                    Family members can interact with your AI presence and access shared memories.
                  </p>
                </Card>
              </div>
              <div className="text-center">
                <Button 
                  onClick={() => {
                    setShowHowItWorksModal(false);
                    setShowGetStartedModal(true);
                  }}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-8"
                >
                  Get Started Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">Login to Memora AI</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowLoginModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-white">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  className="bg-slate-700 border-gray-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-white">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  className="bg-slate-700 border-gray-600 text-white"
                  required
                />
              </div>
              <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700 text-white">
                Login
              </Button>
              <p className="text-center text-gray-400">
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setShowLoginModal(false);
                    setShowSignUpModal(true);
                  }}
                  className="text-purple-400 hover:text-purple-300"
                >
                  Sign up
                </button>
              </p>
            </form>
          </div>
        </div>
      )}

      {/* Sign Up Modal */}
      {showSignUpModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">Start Your Free Trial</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowSignUpModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="mb-4 p-3 bg-green-900/20 border border-green-600 rounded">
              <p className="text-green-400 text-sm">
                ✅ 7-day free trial • No credit card required • Cancel anytime
              </p>
            </div>
            <form onSubmit={handleSignUp} className="space-y-4">
              <div>
                <Label htmlFor="fullName" className="text-white">Full Name</Label>
                <Input
                  id="fullName"
                  type="text"
                  value={signUpForm.fullName}
                  onChange={(e) => setSignUpForm({ ...signUpForm, fullName: e.target.value })}
                  className="bg-slate-700 border-gray-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="signupEmail" className="text-white">Email</Label>
                <Input
                  id="signupEmail"
                  type="email"
                  value={signUpForm.email}
                  onChange={(e) => setSignUpForm({ ...signUpForm, email: e.target.value })}
                  className="bg-slate-700 border-gray-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="signupPassword" className="text-white">Password</Label>
                <Input
                  id="signupPassword"
                  type="password"
                  value={signUpForm.password}
                  onChange={(e) => setSignUpForm({ ...signUpForm, password: e.target.value })}
                  className="bg-slate-700 border-gray-600 text-white"
                  required
                />
              </div>
              <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 text-white">
                Start Free Trial
              </Button>
              <p className="text-center text-gray-400">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setShowSignUpModal(false);
                    setShowLoginModal(true);
                  }}
                  className="text-purple-400 hover:text-purple-300"
                >
                  Login
                </button>
              </p>
            </form>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-700">
              <h2 className="text-xl font-bold text-white">Subscription Management</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowSubscriptionModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <SubscriptionManager onClose={() => setShowSubscriptionModal(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Index;